import { Card, Col, Image, Radio, Row, Table } from 'antd';
import { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import i18n from 'i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { OrderStatisticsType, OrderSummaryType } from 'types/report';
import { thousands } from 'utils/tools';
import { formatCurrencyByStockPlace } from 'utils/stock-place-currency';
import { ShoppingCart } from 'lucide-react';

interface CustomColumnType<T> extends ColumnType<T> {
  hidden?: boolean;
}

interface SalesDailyReportProps {
  loading: boolean;
  orders?: OrderStatisticsType[];
  summary?: OrderSummaryType;
  stockPlace?: string;
}

const SalesDailyReport: React.FC<SalesDailyReportProps> = ({
  loading,
  orders,
  summary,
  stockPlace,
}) => {
  const [salesViewMode, setSalesViewMode] = useState<'staff' | 'order'>(
    'order'
  );

  const renderStatistics = useCallback(
    (
      items: {
        name: string;
        value: React.ReactNode;
        key: string;
      }[]
    ) => {
      return (
        <div className="mb-4 mt-4">
          <Row gutter={[16, 16]} className="flex items-center">
            <Col flex="auto">
              <Card
                bordered={false}
                size="small"
                bodyStyle={{ padding: '16px', backgroundColor: '#EBF5FF' }}
                className="rounded-lg"
              >
                <div
                  className="w-full grid gap-4"
                  style={{
                    gridTemplateColumns:
                      'repeat(auto-fit, minmax(160px, 1fr))',
                  }}
                >
                  {items.map((d, index) => (
                    <div
                      key={index}
                      className="flex justify-center items-center flex-col"
                    >
                      <div className="text-sm text-gray-600 text-center mb-1">
                        {d.name}
                      </div>
                      <div className="text-lg font-semibold text-blue-700">
                        {d.value}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      );
    },
    []
  );

  const currencySwitch = useCallback(
    (value: number) => {
      return formatCurrencyByStockPlace(value, stockPlace);
    },
    [stockPlace]
  );

  const formatCurrencyRecord = useCallback((values?: Record<string, number>) => {
    if (!values) return '-';
    const entries = Object.entries(values);
    if (!entries.length) return '-';
    if (entries.length === 1) {
      const [currency, amount] = entries[0];
      return `${currency} ${thousands(amount)}`;
    }

    return (
      <div className="space-y-1 text-center">
        {entries.map(([currency, amount]) => (
          <div key={currency}>{`${currency} ${thousands(amount)}`}</div>
        ))}
      </div>
    );
  }, []);

  const salesStatsEle = useMemo(() => {
    if (!summary) return null;
    const items = [
      {
        name: i18n.t('total_orders'),
        value: thousands(summary.totalOrders),
        key: 'totalOrders',
      },
      {
        name: i18n.t('total_sales_quantity'),
        value: thousands(summary.totalSalesQuantity),
        key: 'totalSalesQuantity',
      },
      {
        name: i18n.t('total_order_amount'),
        value: currencySwitch(summary.totalOrderAmount),
        key: 'totalOrderAmount',
      },
      {
        name: i18n.t('total_discount_amount'),
        value: currencySwitch(summary.totalDiscountAmount),
        key: 'totalDiscountAmount',
      },
      {
        name: i18n.t('total_tax_amount'),
        value: currencySwitch(summary.totalTaxAmount),
        key: 'totalTaxAmount',
      },
      {
        name: i18n.t('total_pay_amount_statistics'),
        value: currencySwitch(summary.totalPayAmount),
        key: 'totalPayAmount',
      },
      {
        name: i18n.t('total_cost_by_currency'),
        value: formatCurrencyRecord(summary.totalCostByCurrency),
        key: 'totalCostByCurrency',
      },
      {
        name: i18n.t('total_gross_profit_by_currency'),
        value: formatCurrencyRecord(summary.totalGrossProfitByCurrency),
        key: 'totalGrossProfitByCurrency',
      },
    ];
    return renderStatistics(items);
  }, [
    currencySwitch,
    formatCurrencyRecord,
    renderStatistics,
    summary,
  ]);

  const staffNameCountMap = useMemo(() => {
    const map = new Map<string, number>();
    orders?.forEach((order) => {
      const key = order.staffName ?? '';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [orders]);

  const staffSalesByCurrencyMap = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    orders?.forEach((order) => {
      const staffName = order.staffName ?? '';
      if (!map.has(staffName)) {
        map.set(staffName, {});
      }
      const currencyTotals = map.get(staffName)!;
      const currency = order.currency;
      if (!currencyTotals[currency]) {
        currencyTotals[currency] = 0;
      }
      currencyTotals[currency] += order.payAmountActualCurrency;
    });
    return map;
  }, [orders]);

  const createStaffRowSpan = useCallback(
    (record: OrderStatisticsType, index?: number) => {
      if (!record) return {} as { rowSpan?: number };
      if (index === undefined) return {} as { rowSpan?: number };
      if (!orders || !orders.length) return {} as { rowSpan?: number };
      const currentStaffName = record.staffName ?? '';
      const firstIndex = orders.findIndex(
        (order) => (order.staffName ?? '') === currentStaffName
      );
      if (index === firstIndex) {
        return { rowSpan: staffNameCountMap.get(currentStaffName) || 1 };
      }
      return { rowSpan: 0 };
    },
    [orders, staffNameCountMap]
  );

  const columnsRender = useCallback(
    () => ({
      salesPaymentMethod: (
        paymentMethod: Record<string, number>,
        record: OrderStatisticsType
      ) => {
        const entries = Object.entries(paymentMethod || {});
        if (entries.length === 0) return '';
        return entries.length === 1
          ? entries[0][0]
          : entries.map(([method, amount], index) => (
              <div key={index}>{`${method}: ${record.currency} ${thousands(
                amount
              )}`}</div>
            ));
      },
    }),
    []
  );

  const salesColumns: CustomColumnType<OrderStatisticsType>[] = useMemo(() => {
    return [
      {
        title: i18n.t('sales_responsible'),
        dataIndex: 'staffName',
        key: 'staffName',
        onCell: createStaffRowSpan,
      },
      {
        title: i18n.t('VrklWxjXqm'),
        key: 'staffSalesByCurrency',
        render: (_, record) => {
          const currentStaffName = record.staffName ?? '';
          const currencyTotals =
            staffSalesByCurrencyMap.get(currentStaffName) || {};
          const formattedTotals = Object.entries(currencyTotals)
            .map(([currency, total]) => `${currency} ${thousands(total)}`)
            .join('\n');
          return (
            <span style={{ whiteSpace: 'pre-line' }}>{formattedTotals}</span>
          );
        },
        onCell: createStaffRowSpan,
      },
      {
        title: i18n.t('sales_date'),
        dataIndex: 'date',
        key: 'date',
        render: (date: OrderStatisticsType['date']) =>
          dayjs(date).format('YYYY-MM-DD'),
      },
      {
        title: i18n.t('dwawuQNUEi'),
        dataIndex: 'orderId',
        key: 'orderId',
        render: (orderId: OrderStatisticsType['orderId']) => (
          <a href={`/oms/order-view/${orderId}`}>{orderId}</a>
        ),
      },
      {
        title: i18n.t('sales_quantity_sold'),
        dataIndex: 'salesQuantity',
        key: 'salesQuantity',
      },
      {
        title: i18n.t('currency'),
        dataIndex: 'currency',
        key: 'currency',
        hidden: true,
      },
      {
        title: i18n.t('sales_sales_amount_(subtotal)'),
        dataIndex: 'payAmountActualCurrency',
        key: 'payAmountActualCurrency',
        render: (
          payAmountActualCurrency: OrderStatisticsType['payAmountActualCurrency'],
          record: OrderStatisticsType
        ) => `${record.currency} ${thousands(payAmountActualCurrency)}`,
      },
      {
        title: i18n.t('sales_discount_promotion'),
        dataIndex: 'discountAmount',
        key: 'discountAmount',
        render: (discountAmount: OrderStatisticsType['discountAmount']) =>
          currencySwitch(discountAmount),
      },
      {
        title: i18n.t('sales_total'),
        dataIndex: 'totalAmountActualCurrency',
        key: 'totalAmountActualCurrency',
        render: (
          totalAmountActualCurrency: OrderStatisticsType['totalAmountActualCurrency'],
          record: OrderStatisticsType
        ) => `${record.currency} ${thousands(totalAmountActualCurrency)}`,
      },
      {
        title: i18n.t('sales_tax'),
        dataIndex: 'taxAmount',
        key: 'taxAmount',
        render: (
          taxAmount: OrderStatisticsType['taxAmount'],
          record: OrderStatisticsType
        ) => `${record.currency} ${thousands(taxAmount)}`,
      },
    ];
  }, [staffSalesByCurrencyMap, createStaffRowSpan, currencySwitch]);

  const originalSalesColumns: CustomColumnType<OrderStatisticsType>[] =
    useMemo(() => {
      return [
        {
          title: i18n.t('sales_date'),
          dataIndex: 'date',
          key: 'date',
          render: (date: OrderStatisticsType['date']) =>
            dayjs(date).format('YYYY-MM-DD'),
        },
        {
          title: i18n.t('sales_customer_id'),
          dataIndex: 'memberId',
          key: 'memberId',
          render: (memberId: OrderStatisticsType['memberId']) => (
            <a href={`/ums/member-view/${memberId}`}>{memberId}</a>
          ),
        },
        {
          title: i18n.t('sales_product_id'),
          dataIndex: 'productIds',
          key: 'productIds',
          render: (productIds: OrderStatisticsType['productIds']) => (
            <div className="space-y-1">
              {productIds &&
                Array.from(
                  { length: Math.ceil(productIds.length / 3) },
                  (_, i) => (
                    <div key={i} className="flex space-x-2">
                      {productIds.slice(i * 3, i * 3 + 3).map((item) => (
                        <a
                          href={`/pms/product-view/${item.productId}`}
                          key={item.productId}
                        >
                          {item.productId}
                        </a>
                      ))}
                    </div>
                  )
                )}
            </div>
          ),
        },
        {
          title: i18n.t('product_pictures'),
          key: 'product_pictures',
          render: ({ productIds }: OrderStatisticsType) => {
            return (
              <div className="flex gap-2">
                {productIds?.map(({ productId, pic }) => {
                  return (
                    <div key={productId} className="w-16 h-16">
                      {pic ? (
                        <Image
                          src={pic}
                          alt="product"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        '-'
                      )}
                    </div>
                  );
                })}
              </div>
            );
          },
        },
        {
          title: i18n.t('dwawuQNUEi'),
          dataIndex: 'orderId',
          key: 'orderId',
          render: (orderId: OrderStatisticsType['orderId']) => (
            <a href={`/oms/order-view/${orderId}`}>{orderId}</a>
          ),
        },
        {
          title: i18n.t('sales_quantity_sold'),
          dataIndex: 'salesQuantity',
          key: 'salesQuantity',
        },
        {
          title: i18n.t('currency'),
          dataIndex: 'currency',
          key: 'currency',
          hidden: true,
        },
        {
          title: i18n.t('sales_sales_amount_(subtotal)'),
          dataIndex: 'payAmountActualCurrency',
          key: 'payAmountActualCurrency',
          render: (
            payAmountActualCurrency: OrderStatisticsType['payAmountActualCurrency'],
            record: OrderStatisticsType
          ) => `${record.currency} ${thousands(payAmountActualCurrency)}`,
        },
        {
          title: i18n.t('sales_discount_promotion'),
          dataIndex: 'discountAmount',
          key: 'discountAmount',
          render: (discountAmount: OrderStatisticsType['discountAmount']) =>
            currencySwitch(discountAmount),
        },
        {
          title: i18n.t('sales_total'),
          dataIndex: 'totalAmountActualCurrency',
          key: 'totalAmountActualCurrency',
          render: (
            totalAmountActualCurrency: OrderStatisticsType['totalAmountActualCurrency'],
            record: OrderStatisticsType
          ) => `${record.currency} ${thousands(totalAmountActualCurrency)}`,
        },
        {
          title: i18n.t('sales_tax'),
          dataIndex: 'taxAmount',
          key: 'taxAmount',
          render: (
            taxAmount: OrderStatisticsType['taxAmount'],
            record: OrderStatisticsType
          ) => `${record.currency} ${thousands(taxAmount)}`,
        },
        {
          title: i18n.t('sales_payment_method'),
          dataIndex: 'paymentMethod',
          key: 'paymentMethod',
          render: (
            paymentMethod: Record<string, number>,
            record: OrderStatisticsType
          ) => columnsRender().salesPaymentMethod(paymentMethod, record),
        },
        {
          title: i18n.t('sales_responsible'),
          dataIndex: 'staffName',
          key: 'staffName',
          render: (staffName: OrderStatisticsType['staffName']) =>
            staffName ?? '-',
        },
        {
          title: i18n.t('sales_notes'),
          dataIndex: 'note',
          key: 'note',
        },
      ];
    }, [columnsRender, currencySwitch]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" />
          <div className="text-base font-medium">
            {i18n.t('sales_daily_report')}
          </div>
        </div>

        <Radio.Group
          value={salesViewMode}
          onChange={(e) => setSalesViewMode(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="staff">{i18n.t('Gahtpvbfsb')}</Radio.Button>
          <Radio.Button value="order">{i18n.t('ABGYpGErys')}</Radio.Button>
        </Radio.Group>
      </div>

      <Table
        loading={loading}
        pagination={false}
        size="small"
        bordered
        tableLayout="auto"
        columns={
          salesViewMode === 'staff'
            ? salesColumns.filter((d) => !d.hidden)
            : originalSalesColumns.filter((d) => !d.hidden)
        }
        dataSource={orders}
        rowKey={(_, index) => index || 0}
        scroll={{ x: 'max-content' }}
      />
      {salesStatsEle}
    </Card>
  );
};

export default SalesDailyReport;
